import React, { useEffect, useState } from "react";
import Header from "../Header/Header";
import { Autocomplete, Box, Button, Checkbox, CircularProgress, Grid, Snackbar, TextField, Typography } from "@mui/material";
import BasePanel from "../Common/Layout/BasePanel";
import { BasePanelBox } from "../Common/Layout/BasePanelBox";
import { CreatioAccountDTO, CreatioAccountMapDTO, CustomerDTO, SchoolListItem, SiteDTO, SiteID } from "@beable/roster-backend-client-v1";
import BaseSheet from "../Common/Layout/BaseSheet";
import MuiAlert from '@mui/material/Alert';
import RosterBackendService from "../../services/RosterBackendService";
import ConfirmDialog from "../Common/ConfirmDialog";
import CustomerAndSiteSelector from "../Common/CustomerAndSiteSelector";
import { StyledPanelHeader } from "../Common/Layout/PanelHeader";
import { UUIDv1 } from '@beable/mwm-commons-types';
import { sortBy } from 'lodash';
import { cloneDeep } from "lodash";
interface MessageSettings {
  open: boolean;
  message: string;
  severity: 'error' | 'info' | 'success' | 'warning' | undefined;
}

interface SchoolMappingItem {
  id?: string,
  rosterSchool: SchoolListItem;
  creatioAccount: CreatioAccountDTO;
  checked: boolean,
}

const initialCustomerValue: CustomerDTO = new CustomerDTO();
const initialSiteValue: SiteDTO = new SiteDTO();
const initialMessageSettings = {
  open: false,
  message: "",
  severity: "info" as 'error' | 'info' | 'success' | 'warning',
};

export default function CreatioAccountMapping() {
  const [customer, setCustomer] = useState(initialCustomerValue);
  const [site, setSite] = useState(initialSiteValue);
  const [messageSettings, setMessageSettings] = useState<MessageSettings>(initialMessageSettings);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedRosterSchool, setSelectedRosterSchool] = useState<SchoolListItem | null>();
  const [selectedCreatioAccount, setSelectedCreatioAccount] = useState<CreatioAccountDTO | null>();
  const [rosterSchoolCreatioAccountMapping, setRosterSchoolCreatioAccountMapping] = useState<SchoolMappingItem[]>([]);
  const [rosterSchools, setRosterSchools] = useState<SchoolListItem[]>([]);
  const [creatioAccounts, setCreatioAccounts] = useState<CreatioAccountDTO[]>([]);
  const [unavailableRosterSchoolIds, setUnavailableRosterSchoolIds] = useState<Set<string>>(new Set());
  const [allSelected, setAllSelected] = useState<boolean>(false);
  const [someSelected, setSomeSelected] = useState<boolean>(false);
  const [_selectedMappingsCount, setSelectedMappingsCount] = useState<number>(0);
  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        if (site.id) {
          const rosterSchools = await getRosterSchools(site.id);
          const creatioAccounts = await getCreatioAccountSchools(customer.id);
          await loadCreatioAccountMappings(rosterSchools, creatioAccounts);
        } else {
          setRosterSchools([]);
          setCreatioAccounts([]);
          setRosterSchoolCreatioAccountMapping([]);
          setUnavailableRosterSchoolIds(new Set());
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        displayMessage(`Failed to fetch data`, 'error');
        console.error(`Failed to fetch data. Error: ${(error as Error).message}`);
        return [];
      }
    }
    fetchData();
  }, [site]);

  useEffect(() => {
    const addSchoolMapping = async () => {
      if (selectedRosterSchool && selectedCreatioAccount) {
        const previousMappings = cloneDeep(rosterSchoolCreatioAccountMapping);
        const newSchoolMappingItem: SchoolMappingItem = {
          rosterSchool: selectedRosterSchool,
          creatioAccount: selectedCreatioAccount,
          checked: false,
        };
        
        setSelectedRosterSchool(null);
        setSelectedCreatioAccount(null);
        
        setIsLoading(true);
        const saveSuccess = await saveMapping(newSchoolMappingItem);
        setIsLoading(false);
        if (!saveSuccess) {
          setRosterSchoolCreatioAccountMapping(previousMappings);
        }
      }
    }
    addSchoolMapping();
  }, [selectedRosterSchool, selectedCreatioAccount]);

  useEffect(() => {
    const mappingsCount = rosterSchoolCreatioAccountMapping.filter(item => item.checked).length;
    const someSelected = mappingsCount > 0;
    const allSelected = (mappingsCount > 0) && (mappingsCount === rosterSchoolCreatioAccountMapping.length);
    setSelectedMappingsCount(mappingsCount);
    setSomeSelected(someSelected);
    setAllSelected(allSelected);
  }, [rosterSchoolCreatioAccountMapping])

  async function getRosterSchools(siteId: number): Promise<SchoolListItem[]> {
    const response = await RosterBackendService.getSchools(siteId);
    if (response.isErr()) {
      throw response.error;
    }
    return response.value;
  }

  async function getCreatioAccountSchools(customerId: string): Promise<CreatioAccountDTO[]> {
    const response = await RosterBackendService.creatioAccountFindSchoolsByCustomerId(customerId);

    if (response.isErr()) {
      throw response.error;
    }
    return response.value;
  }

  async function loadCreatioAccountMappings(rosterSchools: SchoolListItem[], creatioAccounts: CreatioAccountDTO[]): Promise<void> {
    let remainingData = true;
    let pageNumber = 0;
    const safeguard = 1000;
    const mappings = [];

    while (remainingData && (pageNumber < safeguard)) {
      const response = await RosterBackendService.creatioAccountMapFind({
        siteId: site.id as SiteID,
        pageNumber,
        pageSize: 50
      });

      if (response.isErr()) {
        displayMessage('Failed to load mappings.', 'error');
        console.error(`Failed to load mappings. Error:`, response.error);
        return;
      }

      mappings.push(...response.value.mappings);

      if (!response.value.mappings.length || (response.value.mappings.length < response.value.pageSize)) {
        remainingData = false;
      }
      pageNumber++;
    }

    if (pageNumber >= safeguard) {
      displayMessage(`Page number (${pageNumber}) hit the limit: ${safeguard}`, 'warning');
      console.error(`Page number (${pageNumber}) hit the limit: ${safeguard}`);
      return;
    }

    const schoolMappings: SchoolMappingItem[] = [];
    const unavailableIds = new Set<string>();

    for (const mapping of mappings) {
      const rosterSchool = rosterSchools.find(rosterSchool => rosterSchool.id === mapping.orgId);
      const creatioAccount = creatioAccounts.find(creatioAccount => creatioAccount.id === mapping.creatioAccountId);
      if (creatioAccount && rosterSchool) {
        schoolMappings.push({
          id: mapping.id,
          creatioAccount,
          rosterSchool,
          checked: false,
        });
        unavailableIds.add(rosterSchool.id);
      }
    }

    const orderedRosterSchools = sortBy(rosterSchools, item => item.name);
    const orderedCreatioAccounts = sortBy(creatioAccounts, item => item.name);
    const sortedSchoolMappings = sortBy(schoolMappings, item => item.creatioAccount?.active && item.creatioAccount?.isParticipatingSchool);

    setRosterSchools(orderedRosterSchools);
    setCreatioAccounts(orderedCreatioAccounts);
    setUnavailableRosterSchoolIds(unavailableIds);
    setRosterSchoolCreatioAccountMapping(sortedSchoolMappings);
  }

  async function saveMapping(mapping: SchoolMappingItem): Promise<boolean> {
    if (!mapping.creatioAccount || !mapping.rosterSchool || !site.id) {
      return false;
    }

    try {
      const newMap = new CreatioAccountMapDTO();
      if (mapping.id) newMap.id = UUIDv1(mapping.id);
      newMap.siteId = site.id as SiteID;
      newMap.orgId = UUIDv1(mapping.rosterSchool.id);
      newMap.creatioAccountId = UUIDv1(mapping.creatioAccount.id);

      const response = await RosterBackendService.creatioAccountMapSave(newMap);

      if (response.isErr()) {
        displayMessage('Failed to save mapping.', 'error');
        console.error(`Failed to save mapping. Error:`, response.error);
        return false;
      }

      if (response.isOk()) {
        const currentRosterSchools = rosterSchools;
        const currentCreatioAccounts = creatioAccounts;
        await loadCreatioAccountMappings(currentRosterSchools, currentCreatioAccounts);
        displayMessage('Mapping saved', 'success');
        return true;
      }
      displayMessage('Failed to save mapping', 'error');
      return false;
    } catch (error) {
      displayMessage(`Failed to save mapping`, 'error');
      console.error(`Failed to save mapping. Error: ${(error as Error).message}`);
      return false;
    }
  }

  const handleCustomerChange = (customer: CustomerDTO) => {
    setSite(initialSiteValue);
    setCustomer(customer);
    setSelectedRosterSchool(null);
    setSelectedCreatioAccount(null);
  };

  const handleSiteChange = async (site: SiteDTO) => {
    setSite(site || initialSiteValue);
    setSelectedRosterSchool(null);
    setSelectedCreatioAccount(null);
  }

  const closeMessage = () => {
    setMessageSettings({ ...initialMessageSettings });
  };

  const message = (
    <Snackbar
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      open={messageSettings.open && !isLoading}
      autoHideDuration={3000}
      onClose={closeMessage}
      sx={{ position: 'absolute' }}
    >
      <MuiAlert
        severity={messageSettings.severity}
        variant="filled"
      >
        {messageSettings.message}
      </MuiAlert>
    </Snackbar>
  )

  const handleRosterSchoolChange = (_event: any, selectedRosterSchool: SchoolListItem | null) => {
    setSelectedRosterSchool(selectedRosterSchool);
  }

  const handleCreatioAccountChange = (_event: any, selectedCreatioAccount: CreatioAccountDTO | null) => {
    setSelectedCreatioAccount(selectedCreatioAccount);
  }

  const displayMessage = (message: string, severity: 'error' | 'info' | 'success' | 'warning') => {
    setMessageSettings({
      open: true,
      message,
      severity,
    });
  };

  const rosterSchoolsSelector = (
    <Autocomplete
      data-testid="roster-schools-selector"
      options={rosterSchools.filter(school => !unavailableRosterSchoolIds.has(school.id))}
      value={selectedRosterSchool || null}
      getOptionLabel={(rosterSchoolItem: SchoolListItem) => rosterSchoolItem.name}
      isOptionEqualToValue={(
        rosterSchoolItem: SchoolListItem,
        value: SchoolListItem
      ) => {
        if (rosterSchoolItem.id === value?.id) {
          return true;
        }
        return false;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search for a roster school"
          size="medium"
          label={`Roster School`}
          variant="standard"
        />
      )}
      onChange={handleRosterSchoolChange}
      size="medium"
      data-cy="roster-school-select"
    />
  )

  const creatioAccountsSelector = (
    <Autocomplete
      data-testid="creatio-schools-selector"
      options={creatioAccounts.filter(account => account.active && account.isParticipatingSchool)}
      value={selectedCreatioAccount || null}
      getOptionLabel={(rosterSchoolItem: CreatioAccountDTO) => rosterSchoolItem.name}
      isOptionEqualToValue={(
        rosterSchoolItem: CreatioAccountDTO,
        value: CreatioAccountDTO
      ) => {
        if (rosterSchoolItem.id === value?.id) {
          return true;
        }
        return false;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search for a creatio school"
          size="medium"
          label={`Creatio School`}
          variant="standard"
        />
      )}
      onChange={handleCreatioAccountChange}
      size="medium"
      data-cy="creatio-school-select"
    />
  )

  const handleCheckMapping = (schoolMappingItem: SchoolMappingItem) => {
    schoolMappingItem.checked = !schoolMappingItem.checked;
    setRosterSchoolCreatioAccountMapping([...rosterSchoolCreatioAccountMapping]);
  }

  const handleMappingCreatioAccountChange = async (mapping: SchoolMappingItem, newCreatioAccount: CreatioAccountDTO | null) => {
    if (!mapping || !newCreatioAccount || !mapping.rosterSchool) return;
    
    const mappingIndex = rosterSchoolCreatioAccountMapping.findIndex(m => m.id === mapping.id && m.rosterSchool?.id === mapping.rosterSchool?.id);
    if (mappingIndex >= 0) {
      const previousMappings = cloneDeep(rosterSchoolCreatioAccountMapping);
      const updatedMappings = cloneDeep(rosterSchoolCreatioAccountMapping);
      updatedMappings[mappingIndex].creatioAccount = newCreatioAccount;

      setRosterSchoolCreatioAccountMapping(updatedMappings);

      const saveSuccess = await saveMapping(updatedMappings[mappingIndex]);
      if (!saveSuccess) {
        displayMessage('Failed to update mapping', 'error');
        setRosterSchoolCreatioAccountMapping(previousMappings);
      }
    }
  }

  const selectAll = () => {
    for (const map of rosterSchoolCreatioAccountMapping) {
      map.checked = !allSelected;
    }
    setRosterSchoolCreatioAccountMapping([...rosterSchoolCreatioAccountMapping]);
    setAllSelected(!allSelected)
  }

  function removeItemsAtIndexes<T>(array: T[], indexes: number[]): T[] {
    indexes.sort((a, b) => b - a);

    for (const index of indexes) {
      if (index >= 0 && index < array.length) {
        array.splice(index, 1);
      }
    }

    return array;
  }

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      const selectedMappings: { index: number, mapping: SchoolMappingItem }[] = [];
      rosterSchoolCreatioAccountMapping.forEach((mapping, index) => {
        if (mapping.checked) {
          selectedMappings.push({
            index,
            mapping
          });
        }
      });
      const mappingIdsToDelete = selectedMappings.filter(item => item.mapping.id).map(item => item.mapping.id);

      if (mappingIdsToDelete.length) {
        const response = await RosterBackendService.creatioAccountMapDelete({
          siteId: site.id as SiteID,
          ids: mappingIdsToDelete as UUIDv1[],
        });
        if (response.isErr()) {
          setIsLoading(false);
          displayMessage(`Failed to delete mappings`, 'error');
          console.error(`Failed to delete mappings. Error: ${response.error.message}`);
          return;
        }
        if (response.isOk()) {
          displayMessage('Successfully deleted', 'success');
        }
      }

      const removeMappings = removeItemsAtIndexes(rosterSchoolCreatioAccountMapping, selectedMappings.map(item => item.index));
      const rosterSchoolIdsToRemove = selectedMappings.map(item => item.mapping.rosterSchool.id);
      const updatedUnavailableIds = new Set(unavailableRosterSchoolIds);
      rosterSchoolIdsToRemove.forEach(id => updatedUnavailableIds.delete(id));
      
      setIsLoading(false);
      setRosterSchoolCreatioAccountMapping([...removeMappings]);
      setUnavailableRosterSchoolIds(updatedUnavailableIds);
    } catch (error) {
      setIsLoading(false);
      displayMessage(`Failed to delete mappings`, 'error');
      console.error(`Failed to delete mappings. Error: ${(error as Error).message}`);
    }
  }

  const schoolMapping = (
    <Box
      sx={{
        mb: 5,
        position: "relative"
      }}>
      <Grid container>
        <Grid size={6}>
          <Box sx={{
            display: (rosterSchoolCreatioAccountMapping.length > 0) ? 'block' : 'none'
          }}>
            <Checkbox
              checked={allSelected}
              size='small'
              color="primary"
              sx={{ padding: '4px' }}
              onChange={selectAll}
              onClick={event => event.stopPropagation()}
            />
            <Button
              variant="text"
              onClick={selectAll}
            >
              Select All
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Grid container sx={{
        alignItems: "top"
      }}>
        <Grid size={1} />
        <Grid
          size={5}
          sx={{
            pr: 2,
            pb: 3,
            borderRight: "1px solid gray"
          }}>
          {rosterSchoolsSelector}
        </Grid>
        <Grid
          size={5}
          sx={{
            pl: 4,
            pb: 3
          }}>
          {creatioAccountsSelector}
        </Grid>
      </Grid>
      <Grid container>
        {
          rosterSchoolCreatioAccountMapping.map((mapping, index) => (
            <Grid
              container
              key={index}
              onClick={event => {
                event.stopPropagation();
                handleCheckMapping(mapping);
              }}
              sx={{
                pl: 0,
                pr: 0
              }}>
              <Grid size={1} sx={{
                justifyContent: "center"
              }}>
                <Checkbox
                  checked={mapping.checked}
                  size='small'
                  color="primary"
                  sx={{ padding: '4px' }}
                  onChange={() => handleCheckMapping(mapping)}
                  onClick={event => event.stopPropagation()}
                />
              </Grid>
              <Grid size={5} sx={{
                borderRight: "1px solid gray"
              }}>
                {
                  mapping.rosterSchool.name
                }
              </Grid>
              <Grid onClick={event => event.stopPropagation()} size={5} sx={{
                pl: 4
              }}>
                <Autocomplete
                  options={creatioAccounts.filter(account => 
                    (account.active && account.isParticipatingSchool) || (account.id === mapping.creatioAccount?.id)
                  )}
                  value={mapping.creatioAccount || null}
                  getOptionLabel={(account: CreatioAccountDTO) => {
                    if (!(account.active && account.isParticipatingSchool)) {
                      return `${account.name} Inactive`;
                    }
                    return account.name;
                  }}
                  isOptionEqualToValue={(account: CreatioAccountDTO, value: CreatioAccountDTO) => account.id === value?.id}
                  getOptionDisabled={(account: CreatioAccountDTO) => !(account.active && account.isParticipatingSchool)}
                  onChange={(_event, newValue) => {
                    if (newValue && !(newValue.active && newValue.isParticipatingSchool)) {
                      return;
                    }
                    handleMappingCreatioAccountChange(mapping, newValue);
                  }}
                  selectOnFocus={false}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="standard"
                      placeholder="Select creatio account"
                      slotProps={{
                        ...params.slotProps,

                        input: {
                          ...params.slotProps.input,
                          disableUnderline: true,
                        }
                      }}
                      sx={{
                        '& .MuiInputBase-input': {
                          color: !mapping.creatioAccount 
                            ? 'red' 
                            : !(mapping.creatioAccount.active && mapping.creatioAccount.isParticipatingSchool) 
                              ? 'red' 
                              : 'inherit',
                        },
                      }}
                    />
                  )}
                  renderOption={(props, account: CreatioAccountDTO) => (
                    <li {...props} key={account.id}>
                      {!(account.active && account.isParticipatingSchool) ? (
                        <Typography component="span" sx={{ color: 'red' }}>{account.name} Inactive</Typography>
                      ) : (
                        account.name
                      )}
                    </li>
                  )}
                />
              </Grid>
            </Grid>
          ))
        }
      </Grid>
      {
        isLoading ?
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: '#f5f5f57a',
          }}>
            <Box sx={{
              display: 'flex',
              width: '100%',
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <CircularProgress disableShrink />
            </Box>
          </Box>
          :
          null
      }
    </Box>
  );

  const buttons = (
    <Grid
      container
      columnSpacing={3}
      sx={{
        justifyContent: "center"
      }}
    >
      <Grid>
        <Button
          variant="contained"
          disabled={!someSelected}
          onClick={() => setOpenConfirmDeleteDialog(true)}
          color="secondary"
        >
          Delete
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <div>
      <Header />
      <Grid container>
        <div id='account-mapping-sidebar'>
          <BasePanel>
            <BasePanelBox>
              <StyledPanelHeader header="Account Mapping" />
              <CustomerAndSiteSelector
                onCustomerSelect={handleCustomerChange}
                onSiteSelect={handleSiteChange}
              />
            </BasePanelBox>
          </BasePanel>
        </div>
        <div id='account-mapping-content'>
          <BaseSheet maxWidth="lg">
            {schoolMapping}
            {buttons}
            {message}
            <ConfirmDialog
              open={openConfirmDeleteDialog}
              header='Confirm Deletion'
              message={`Are you sure you want to delete the selected account mappings?`}
              confirmString="Continue"
              cancelString="Cancel"
              onCancel={() => setOpenConfirmDeleteDialog(false)}
              onConfirm={() => {
                setOpenConfirmDeleteDialog(false);
                handleDelete();
              }}
            />
          </BaseSheet>
        </div>
      </Grid>
    </div>
  );
}